import { DateTime } from 'beesoft-components';
import clsx from 'clsx';
import { fromZonedTime } from 'date-fns-tz';
import type { Ref } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { detectIfTruncated, getBrowserLanguage } from '../../common-functions';
import Tooltip from '../../tooltip-component/tooltip.component';
import type { DataGridDataCellBaseProps } from '../data-grid-settings.interface';

export interface DataGridDataDateCellProps extends DataGridDataCellBaseProps<string | Date> {
  isEditing?: boolean;
  dateOnly?: boolean;
  timeOnly?: boolean;
  width?: number;
}

function DataGridDataDateCell(
  { value, isEditing = false, dateOnly = false, timeOnly = false, width }: DataGridDataDateCellProps,
  forwardRef: Ref<HTMLDivElement>
) {
  const cellRef = useRef<HTMLDivElement>(null);
  const locale = useRef(getBrowserLanguage());
  const [isTruncated, setIsTruncated] = useState(false);

  useImperativeHandle(forwardRef, () => cellRef.current as HTMLDivElement);

  useEffect(() => {
    if (cellRef.current) {
      setIsTruncated(detectIfTruncated(cellRef.current));
    }
  }, [width]);

  const renderDateTimeText = () => {
    if (value) {
      if (timeOnly === true) {
        // According to https://myosh.atlassian.net/browse/MYOSH-1815?focusedCommentId=18292,
        // the timefield column is always in `HH:mm` format
        return value;
      } else {
        const utcDate = fromZonedTime(value, Intl.DateTimeFormat().resolvedOptions().timeZone);

        // Check if the date is valid to prevent RangeError
        if (isNaN(utcDate.getTime())) {
          return '-';
        }

        if (dateOnly === true) {
          return utcDate.toLocaleDateString(locale.current);
        } else {
          return utcDate.toLocaleString(locale.current);
        }
      }
    }
    return '';
  };

  const inputStyles = clsx('text-sm shadow-none focus:outline-hidden', {
    'border-transparent bg-transparent cursor-default leading-4': !isEditing,
    '': isEditing,
  });

  const cellValue = renderDateTimeText();

  // TODO: When editing is implemented look at this again since we may want the date/time to look different
  return !isEditing ? (
    <Tooltip
      description={String(cellValue)}
      tooltipClassName="bg-mono-1"
      disabled={!isTruncated}
      tooltipZIndexCheck={true}
    >
      {/*  NOTE: if modifying the padding 'pl-2', please remember to update all other cells as well as 'CELL_PADDING_LEFT_PX' */}
      <div className="pl-2 truncate" data-testid="data-grid-cell-content" ref={cellRef}>
        {String(cellValue)}
      </div>
    </Tooltip>
  ) : (
    <DateTime
      value={value}
      readOnly={!isEditing}
      iconPosition={2}
      dateSelection={dateOnly === true ? 1 : timeOnly === true ? 2 : 0}
      className={inputStyles}
    />
  );
}

export default forwardRef(DataGridDataDateCell);
